import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Play, Heart, Share2, ListMusic } from 'lucide-react';
import { useAuth } from '../contexts/AppContext';
import { useLikeAuthGate } from './AuthPromptModal';

/**
 * Rail cards lifted out of HomePage so the /discover/trending and
 * /discover/latest destination pages can reuse the EXACT same component.
 *
 * IMPORTANT: do not change the rendering here. HomePage rails are pixel-locked
 * to these markups and the user has explicitly asked the Home screen layout
 * NOT to be modified. This file is purely a relocation — the same JSX that
 * used to live inline in HomePage.js.
 */

const formatCount = (n) => {
  if (!n) return '0';
  if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
  if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
  return n.toString();
};

// ─── Trending Video Card ────────────────────────────────────────────────
// Portrait 9:16, fixed-width on the home rail (150-165px). Share, queue
// (playlist), and like icons on top, title/creator + view count on the
// bottom gradient. Used by Home's "Trending Now" rail AND by the
// /discover/trending destination grid.
export const TrendingCard = ({ video, thumbnail, onClick, onArtistClick, onLike, onPlaylist, onShare }) => {
  // eslint-disable-next-line no-unused-vars
  const navigate = useNavigate();
  const { user } = useAuth();
  const requireAuthForLike = useLikeAuthGate();
  const [liked, setLiked] = useState(video.is_liked || false);
  const [likeCount, setLikeCount] = useState(video.likes || 0);

  const handleLike = (e) => {
    e.stopPropagation();
    // Centralised gate — if not signed in, fire the shared modal and bail
    // BEFORE flipping any UI state. This prevents phantom likes that look
    // real but vanish (and would otherwise inflate Trending counts).
    if (!requireAuthForLike(user, video?.id)) return;
    const newLiked = !liked;
    setLiked(newLiked);
    setLikeCount(prev => newLiked ? prev + 1 : Math.max(0, prev - 1));
    if (onLike) onLike(video.id, newLiked);
  };

  const isNew = video.created_at && (Date.now() - new Date(video.created_at).getTime()) < 7 * 86400000;

  return (
    <div
      className="flex-shrink-0 w-[150px] sm:w-[165px] cursor-pointer group"
      onClick={onClick}
      data-testid={`trending-${video.id}`}
    >
      <div className="relative w-full aspect-[9/16] rounded-xl overflow-hidden bg-gray-800">
        <img
          src={thumbnail}
          alt={video.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        {/* Top action buttons */}
        <div className="absolute top-2 left-2 right-2 flex justify-between items-center">
          <div className="flex gap-1.5">
            <button
              onClick={(e) => { e.stopPropagation(); if (onShare) onShare(video); }}
              className="w-7 h-7 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center"
              data-testid={`share-btn-${video.id}`}
            >
              <Share2 className="w-3 h-3 text-white" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); if (onPlaylist) onPlaylist(video); }}
              className="w-7 h-7 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center"
              data-testid={`playlist-btn-${video.id}`}
            >
              <ListMusic className="w-3 h-3 text-white" />
            </button>
          </div>
          <button
            onClick={handleLike}
            className={`flex items-center gap-1 px-2 py-1 rounded-full backdrop-blur-sm text-[10px] font-semibold ${
              liked ? 'bg-red-500/30 text-red-400' : 'bg-black/50 text-white'
            }`}
            data-testid={`like-btn-${video.id}`}
          >
            <Heart className={`w-3 h-3 ${liked ? 'fill-red-400' : ''}`} />
            {likeCount > 0 && formatCount(likeCount)}
          </button>
        </div>
        {/* Bottom info */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 to-transparent p-2.5">
          <p className="text-xs font-bold text-white truncate">{video.title}</p>
          <button
            onClick={(e) => { e.stopPropagation(); if (onArtistClick) onArtistClick(video); }}
            className="text-[11px] text-gray-300 hover:text-white mt-0.5"
          >
            {video.artist}
          </button>
          <div className="flex items-center gap-1 mt-1 text-gray-400">
            {(video.views || 0) >= 100 ? (
              <>
                <Play className="w-2.5 h-2.5" />
                <span className="text-[10px]">{formatCount(video.views)} views</span>
              </>
            ) : isNew ? (
              <span className="text-[10px] text-orange-400 font-medium">NEW</span>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Latest Upload Card ─────────────────────────────────────────────────
// Smaller portrait 9:16. NEW badge, title + creator. Used by Home's
// "Latest Uploads" rail AND by the /discover/latest destination grid.
export const LatestCard = ({ video, thumbnail, onClick }) => {
  const isNew = video.created_at && (Date.now() - new Date(video.created_at).getTime()) < 7 * 86400000;
  return (
    <div
      className="flex-shrink-0 w-[105px] sm:w-[115px] cursor-pointer group"
      onClick={onClick}
      data-testid={`latest-${video.id}`}
    >
      <div className="relative w-full aspect-[9/16] rounded-lg overflow-hidden bg-gray-800">
        <img
          src={thumbnail}
          alt={video.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        {isNew && (
          <div className="absolute top-1.5 left-1.5 px-1 py-0.5 rounded bg-orange-500/80 text-[9px] text-white font-bold">
            NEW
          </div>
        )}
        {/* Bottom info */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 to-transparent p-2">
          <p className="text-[10px] font-bold text-white truncate">{video.title}</p>
          <p className="text-[9px] text-gray-400 truncate">{video.artist}</p>
        </div>
      </div>
    </div>
  );
};
